package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.HoursRange
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import kotlinx.datetime.LocalDate

/*
 * SH-172: the team's answers for one day, read while the demand is built (the skeleton editor)
 * and while people are assigned (the roster builder's day): how many are available in each of the
 * workplace's availability slots ("Rano 4 · Popołudnie 2 · Wieczór 1"), how many have not answered,
 * the slots whose planned shifts outnumber the people available, and who is who.
 */

/** One person's answer for the day, as the day's list shows it. */
data class PersonDayAnswer(
    val user: User,
    /** Available, no answer or unavailable (approved leave counts as unavailable). */
    val answer: DayAnswer,
    /** What they wrote for the day (slot, hours, comment); null: nothing. */
    val preference: DayPreference?,
    /** The slots they can work that day (every slot when available without a slot or hours). */
    val slotIds: Set<String>,
    /** An approved absence covering the day. */
    val absence: Absence?
)

/** One slot of the day: those available in it and the shifts planned in it. */
data class SlotAnswers(
    val slot: AvailabilitySlot,
    val available: Int,
    /** Shifts of the day (open and assigned) that fall mostly in this slot. */
    val planned: Int
) {
    /** More shifts planned than people available: a gentle warning, nothing is refused. */
    val overDemand: Boolean get() = planned > available
}

/** The day's answers: the slots' counts, the people without an answer, and everyone, best first. */
data class DayAnswerSummary(
    val date: LocalDate,
    val slots: List<SlotAnswers>,
    /** Everyone of the team, available first, then no answer, then unavailable; by name within each. */
    val people: List<PersonDayAnswer>
) {
    val noAnswer: Int get() = people.count { it.answer == DayAnswer.NO_ANSWER }
    val availableCount: Int get() = people.count { it.answer == DayAnswer.AVAILABLE }
    val unavailableCount: Int get() = people.count { it.answer == DayAnswer.UNAVAILABLE }

    /** The slots whose planned shifts outnumber the people available. */
    val warnings: List<SlotAnswers> get() = slots.filter { it.overDemand }

    /**
     * The day's people in the three groups of the list ([DayAnswer] order), each by name. With
     * [slotId], "available" means available in that slot: those available only at other times of
     * the day are listed as unavailable for it (their answer still says when they can).
     */
    fun groups(slotId: String? = null): Map<DayAnswer, List<PersonDayAnswer>> {
        val grouped = people.groupBy { person ->
            if (slotId != null && person.answer == DayAnswer.AVAILABLE && slotId !in person.slotIds) DayAnswer.UNAVAILABLE else person.answer
        }
        return DayAnswer.entries.associateWith { answer -> grouped[answer].orEmpty().sortedBy { it.user.displayName.lowercase() } }
    }
}

object DayAnswers {

    /**
     * The answers of [team] (active members of [workplaceId]) for [date]:
     * - an approved absence that day: unavailable, whatever the form says;
     * - the day marked available or unavailable: as marked;
     * - the day left untouched in a sent form: available all day (the form showed it so, as the
     *   capacity counts it, SH-48); untouched in an unsent form, or no form: no answer.
     *
     * An available person counts in the slots their hours (or their preferred slot's times)
     * overlap; without either, in every slot. A shift counts in the slot it overlaps most.
     */
    fun of(
        date: LocalDate,
        team: List<User>,
        workplaceId: String,
        availability: Map<String, Availability>,
        slots: List<AvailabilitySlot>,
        absences: List<Absence>,
        dayShifts: List<Shift>
    ): DayAnswerSummary {
        val iso = date.toString()
        val people = team
            .filter { it.engagements[workplaceId]?.isActive == true }
            .map { user ->
                val form = availability[user.uid]
                val preference = form?.days?.get(date.day)
                val absence = absences.firstOrNull {
                    it.userId == user.uid && it.status == AbsenceStatus.APPROVED &&
                        (it.workplaceId == null || it.workplaceId == workplaceId) &&
                        it.startDate.take(10) <= iso && iso <= it.endDate.take(10)
                }
                val answer = when {
                    absence != null -> DayAnswer.UNAVAILABLE
                    preference?.status == AvailabilityStatus.AVAILABLE -> DayAnswer.AVAILABLE
                    preference?.status == AvailabilityStatus.UNAVAILABLE -> DayAnswer.UNAVAILABLE
                    form?.isFilled == true -> DayAnswer.AVAILABLE
                    else -> DayAnswer.NO_ANSWER
                }
                PersonDayAnswer(
                    user = user,
                    answer = answer,
                    preference = preference,
                    slotIds = if (answer == DayAnswer.AVAILABLE) slotsOf(preference, slots) else emptySet(),
                    absence = absence
                )
            }
            .sortedWith(compareBy<PersonDayAnswer> { it.answer.ordinal }.thenBy { it.user.displayName.lowercase() })
        val plannedBySlot = dayShifts.mapNotNull { slotOf(it, slots) }.groupingBy { it }.eachCount()
        return DayAnswerSummary(
            date = date,
            slots = slots.map { slot ->
                SlotAnswers(slot, available = people.count { slot.id in it.slotIds }, planned = plannedBySlot[slot.id] ?: 0)
            },
            people = people
        )
    }

    /** Every day of [days] (see [of]); the shifts are the month's, by date. */
    fun forDays(
        days: List<LocalDate>,
        team: List<User>,
        workplaceId: String,
        availability: Map<String, Availability>,
        slots: List<AvailabilitySlot>,
        absences: List<Absence>,
        shiftsByDate: Map<LocalDate, List<Shift>>
    ): Map<LocalDate, DayAnswerSummary> =
        days.associateWith { of(it, team, workplaceId, availability, slots, absences, shiftsByDate[it].orEmpty()) }

    /** The slots an available day covers: those its hours, or its preferred slot, overlap; all without either. */
    fun slotsOf(preference: DayPreference?, slots: List<AvailabilitySlot>): Set<String> {
        val windows: List<IntRange> = preference?.hours?.mapNotNull { it.minutes }?.takeIf { it.isNotEmpty() }
            ?: preference?.preferredSlotId?.let { id -> slots.firstOrNull { it.id == id } }?.let { listOfNotNull(minutesOf(it)) }
            ?: return slots.map { it.id }.toSet()
        if (windows.isEmpty()) return slots.map { it.id }.toSet()
        return slots.filter { slot ->
            val range = minutesOf(slot) ?: return@filter false
            windows.any { overlap(it, range) > 0 }
        }.map { it.id }.toSet()
    }

    /** The slot [shift] overlaps most (the first on a tie); null when it overlaps none. */
    fun slotOf(shift: Shift, slots: List<AvailabilitySlot>): String? {
        val times = shiftMinutes(shift) ?: return null
        return slots
            .mapNotNull { slot -> minutesOf(slot)?.let { slot.id to overlap(times, it) } }
            .filter { it.second > 0 }
            .maxByOrNull { it.second }
            ?.first
    }

    /** A slot's minutes from midnight; one ending at or before its start runs past midnight. */
    private fun minutesOf(slot: AvailabilitySlot): IntRange? {
        val start = HoursRange.minutesOf(slot.startTime) ?: return null
        var end = HoursRange.minutesOf(slot.endTime) ?: return null
        if (end <= start) end += DAY
        return start until end
    }

    /** The shift's planned minutes from its day's midnight (an overnight shift runs past 1440). */
    private fun shiftMinutes(shift: Shift): IntRange? {
        val start = clock(shift.plannedTime.start) ?: return null
        var end = clock(shift.plannedTime.end) ?: return null
        if (shift.plannedTime.end.take(10) > shift.date.take(10) || end <= start) end += DAY
        return start until end
    }

    /** "2026-02-01T08:00:00" (or "08:00") to minutes from midnight. */
    private fun clock(value: String): Int? = HoursRange.minutesOf(value.substringAfter('T', value).take(5))

    private fun overlap(a: IntRange, b: IntRange): Int =
        (minOf(a.last, b.last) - maxOf(a.first, b.first) + 1).coerceAtLeast(0)

    private const val DAY = 24 * 60
}
